import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import api from '../services/api';
import { toast } from 'react-toastify';
import { APP_VERSION } from '../utils/version';
import { formatSignedLeaveDays, leaveUsageRatePercent } from '../utils/leaveDaysFormat';
import NoTranslate from '../components/NoTranslate';
import { useLeaveTypes } from '../hooks/useLeaveTypes';
import { formatDisplayDate, resolveLeaveTypeName } from '../utils/localeFormat';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

function Dashboard() {
  const leaveTypes = useLeaveTypes();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/user');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div className="alert alert-danger">Failed to load data</div>;
  }

  const { leaveBalance, monthlyApplications, stats, monthlyUsage } = dashboardData;

  const usedDays = Number(leaveBalance.used_days);
  const usedSafe = Number.isFinite(usedDays) ? usedDays : 0;
  const remainingRaw = Number(leaveBalance.remaining_days);
  const remainingSafe = Number.isFinite(remainingRaw) ? remainingRaw : 0;
  // Chart: treat negative remaining like 0 (per product decision); text still shows actual below.
  const remainingForChart = Math.max(0, remainingSafe);

  const leaveBalanceData = {
    labels: ['Used', 'Remaining'],
    datasets: [
      {
        data: [usedSafe, remainingForChart],
        backgroundColor: ['#dc3545', '#28a745'],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  // Monthly Usage Chart
  const monthlyChartData = {
    labels: monthlyUsage && monthlyUsage.length > 0 ? monthlyUsage.map(m => m.monthName) : [],
    datasets: [
      {
        label: 'Paid Leave Days Used',
        data: monthlyUsage && monthlyUsage.length > 0 ? monthlyUsage.map(m => {
          if (!m.leave_types || !Array.isArray(m.leave_types)) return 0;
          const paidLeave = m.leave_types.find(lt => lt.code === 'PAID');
          return paidLeave ? paidLeave.days : 0;
        }) : [],
        backgroundColor: '#007bff',
        borderRadius: 4
      }
    ]
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'Pending', class: 'badge-warning' },
      approved: { text: 'Approved', class: 'badge-success' },
      rejected: { text: 'Rejected', class: 'badge-danger' }
    };
    const statusInfo = statusMap[status] || { text: status, class: 'badge-secondary' };
    return (
      <NoTranslate className={`badge ${statusInfo.class}`}>{statusInfo.text}</NoTranslate>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Dashboard</h1>
          <small className="text-muted">{APP_VERSION}</small>
        </div>
        <Link to="/apply" className="btn btn-primary">
          New Application
        </Link>
      </div>

      {/* Paid Leave Balance Card */}
      <div className="grid grid-3 mb-4">
        <div className="card">
          <h3>Paid Leave Balance</h3>
          <div className="grid grid-2">
            <div>
              <div style={{ width: '150px', height: '150px', margin: '0 auto' }}>
                <Doughnut 
                  data={leaveBalanceData}
                  options={{
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            return `${context.label}: ${context.parsed} days`;
                          }
                        }
                      }
                    },
                    maintainAspectRatio: false
                  }}
                />
              </div>
            </div>
            <div>
              <p><strong>Allocated:</strong> {formatSignedLeaveDays(leaveBalance.allocated_days)} days</p>
              <p><strong>Used:</strong> {formatSignedLeaveDays(leaveBalance.used_days)} days</p>
              <p><strong>Remaining:</strong> {formatSignedLeaveDays(leaveBalance.remaining_days)} days</p>
              <p><strong>Usage Rate:</strong> {leaveUsageRatePercent(leaveBalance.used_days, leaveBalance.allocated_days).toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="card">
          <h3>Application Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{stats.approved}</div>
              <div className="stat-label">Approved</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.pending}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.rejected}</div>
              <div className="stat-label">Rejected</div>
            </div>
          </div>
          
          {/* Leave Type Statistics */}
          {stats.leaveTypeStats && (
            <div className="leave-type-stats">
              <h4>Leave Type Breakdown (This Year)</h4>
              <div className="leave-stats-grid">
                {Object.entries(stats.leaveTypeStats).map(([leaveType, data]) => (
                  <div key={leaveType} className="leave-stat-item">
                    <NoTranslate as="div" className="leave-type-name">{data.name}</NoTranslate>
                    <div className="leave-type-days">{data.days} days</div>
                    <div className="leave-type-applications">{data.applications} applications</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3>This Month's Applications</h3>
          {monthlyApplications.length > 0 ? (
            <div className="applications-summary">
              {monthlyApplications.slice(0, 3).map(app => (
                <div key={app.id} className="application-item">
                  <div className="application-date">
                    {formatDisplayDate(app.start_date)}
                    {app.start_date !== app.end_date && 
                      ` - ${formatDisplayDate(app.end_date)}`
                    }
                  </div>
                  <div className="application-type">
                    <NoTranslate>{resolveLeaveTypeName(app, leaveTypes)}</NoTranslate>
                  </div>
                  <div>{getStatusBadge(app.status)}</div>
                </div>
              ))}
              {monthlyApplications.length > 3 && (
                <Link to="/my-applications" className="view-all-link">
                  View All ({monthlyApplications.length} items)
                </Link>
              )}
            </div>
          ) : (
            <p className="text-muted">No applications this month</p>
          )}
        </div>
      </div>

      {/* Monthly Usage Chart */}
      <div className="card">
        <h3>Monthly Leave Usage</h3>
        <div style={{ height: '300px' }}>
          <Bar 
            data={monthlyChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      return `${context.parsed.y} days`;
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    stepSize: 1
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <style>{`
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          text-align: center;
        }
        
        .leave-type-stats {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #dee2e6;
        }
        
        .leave-type-stats h4 {
          margin-bottom: 1rem;
          color: #495057;
        }
        
        .leave-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }
        
        .leave-stat-item {
          padding: 1rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 8px;
          border-left: 4px solid #007bff;
          text-align: center;
        }
        
        .leave-type-name {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.5rem;
        }
        
        .leave-type-days {
          font-size: 1.2rem;
          font-weight: bold;
          color: #007bff;
          margin-bottom: 0.25rem;
        }
        
        .leave-type-applications {
          font-size: 0.9rem;
          color: #6c757d;
        }
        
        .stat-item {
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 8px;
        }
        
        .stat-number {
          font-size: 2rem;
          font-weight: bold;
          color: #007bff;
        }
        
        .stat-label {
          font-size: 0.9rem;
          color: #6c757d;
          margin-top: 0.5rem;
        }
        
        .applications-summary {
          space-y: 0.5rem;
        }
        
        .application-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 1px solid #eee;
        }
        
        .application-item:last-child {
          border-bottom: none;
        }
        
        .application-date {
          font-size: 0.9rem;
          color: #495057;
        }
        
        .application-type {
          font-weight: 500;
          color: #007bff;
        }
        
        .view-all-link {
          display: block;
          text-align: center;
          margin-top: 1rem;
          color: #007bff;
          text-decoration: none;
        }
        
        .view-all-link:hover {
          text-decoration: underline;
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .application-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }
        }
      `}</style>
    </div>
  );
}

export default Dashboard;